import React, { useState, useEffect, useCallback } from "react";
import {
  IconButton,
  CircularProgress,
  Alert,
  Avatar,
  Snackbar,
  Dialog,
  DialogContent,
  Tooltip,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import PropTypes from "prop-types";

import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import DataTable from "shared/components/Tables/DataTable";
import CustomModal from "shared/components/MDmodal/CustomModal";
import FormZone from "../Form/FormZone";
import { getAllZone } from "modules/bitacora/services/Configuracion/Zona/zonaService";
import { deleteZoneService } from "modules/bitacora/services/Configuracion/Zona/zonaService";
import { updateZoneService } from "modules/bitacora/services/Configuracion/Zona/zonaService";

function TablaZone({ refreshFlag }) {
  const [zones, setZones] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [zoneSelect, setZoneSelect] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [zoneToDelete, setZoneToDelete] = useState(null);

  const showNotification = useCallback((message, severity = "success") => {
    setNotification({
      open: true,
      message,
      severity,
    });
  }, []);

  const handleCloseNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  const fetchZone = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllZone();
      const parsedData = typeof data === "string" ? JSON.parse(data) : data;
      setZones(parsedData || []);
    } catch (err) {
      console.error("❌ Error al obtener zonas:", err);
      showNotification("Error al cargar zonas", "error");
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    fetchZone();
  }, [refreshFlag]);

  const handleEditar = useCallback((zona) => {
    setZoneSelect(zona);
    setOpenModal(true);
  }, []);

  const handleEliminarClick = useCallback((id) => {
    setZoneToDelete(id);
    setOpenDeleteDialog(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!zoneToDelete) return;
    try {
      setLoading(true);
      await deleteZoneService(zoneToDelete);
      await fetchZone();
      showNotification("Zona eliminada correctamente", "success");
    } catch (err) {
      console.error("Error al eliminar zona:", err);
      const errorMessage = err.message?.includes("token")
        ? "Su sesión ha expirado. Por favor inicie sesión nuevamente."
        : "Error al eliminar zona";
      showNotification(errorMessage, "error");
    } finally {
      setLoading(false);
      setOpenDeleteDialog(false);
      setZoneToDelete(null);
    }
  }, [zoneToDelete, fetchZone, showNotification]);

  const handleActualizar = useCallback(
    async (datos) => {
      if (!zoneSelect) return;
      try {
        setLoading(true);
        await updateZoneService(zoneSelect.id, {
          commerceId: datos.commerceId,
          title: datos.title,
          description: datos.description,
          status: datos.status,
        });
        await fetchZone();
        setOpenModal(false);
        showNotification("Zona actualizada correctamente", "success");
      } catch (err) {
        console.error("Error al actualizar zona:", err);
        const message = err.response?.data?.message || "Error al actualizar zona";
        showNotification(message, "error");
      } finally {
        setLoading(false);
      }
    },
    [zoneSelect, fetchZone, showNotification]
  );

  const columns = [
    { Header: "Nombre", accessor: "title", width: "30%" },
    { Header: "Comercio", accessor: "commerceId", width: "40%" },
    { Header: "Descripción", accessor: "description", width: "30%" },
    { Header: "Estado", accessor: "status", width: "40%" },
    { Header: "Acciones", accessor: "acciones", align: "center", width: "10%" },
  ];

  const rows = zones.map((zone) => ({
    title: (
      <MDTypography variant="button" fontWeight="medium">
        {zone.title || "Sin nombre"}
      </MDTypography>
    ),
    commerceId: (
      <MDTypography variant="caption" color="text" fontWeight="medium">
        {zone.commerce.name || "Sin comercio"}
      </MDTypography>
    ),
    description: (
      <MDTypography variant="caption" color="text" fontWeight="medium">
        {zone.description || "Sin descripción"}
      </MDTypography>
    ),
    status: (
      <MDTypography
        variant="caption"
        color={zone.status === true ? "success" : "error"}
        fontWeight="medium"
      >
        {zone.status === true ? "Activo" : "Inactivo"}
      </MDTypography>
    ),
    acciones: (
      <MDBox display="flex" justifyContent="center">
        <Tooltip title="Editar Zona">
          <IconButton color="info" onClick={() => handleEditar(zone)}>
            <Edit />
          </IconButton>
        </Tooltip>
        <Tooltip title="Eliminar Zona">
          <IconButton color="error" onClick={() => handleEliminarClick(zone.id)}>
            <Delete />
          </IconButton>
        </Tooltip>
      </MDBox>
    ),
  }));

  return (
    <>
      <MDBox
        mx={-1}
        mt={-1}
        py={2}
        px={2}
        variant="gradient"
        bgColor="success"
        borderRadius="lg"
        coloredShadow="info"
      >
        <MDTypography variant="h6" color="white">
          Gestión de Zonas
        </MDTypography>
      </MDBox>

      <MDBox pt={1} px={-2}>
        {loading ? (
          <MDBox display="flex" justifyContent="center" p={3}>
            <CircularProgress color="info" />
          </MDBox>
        ) : error ? (
          <MDBox p={2}>
            <Alert severity="error">{error}</Alert>
          </MDBox>
        ) : (
          <DataTable
            table={{ columns, rows }}
            isSorted
            entriesPerPage={false}
            showTotalEntries
            noEndBorder
          />
        )}
      </MDBox>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={openDeleteDialog}
        onClose={() => !loading && setOpenDeleteDialog(false)}
        PaperProps={{
          sx: { minWidth: "400px", borderRadius: "12px", padding: "16px" },
        }}
      >
        <MDBox display="flex" alignItems="center" mb={2}>
          <Avatar sx={{ bgcolor: "error.light", mr: 2 }}>
            <Delete color="error" />
          </Avatar>
          <MDTypography variant="h6" fontWeight="medium">
            Confirmar eliminación
          </MDTypography>
        </MDBox>

        <DialogContent>
          <MDTypography variant="body2">Esta acción eliminará la zona del sistema.</MDTypography>
          <MDTypography variant="body2" fontWeight="bold" mt={1}>
            ¿Deseas continuar?
          </MDTypography>
        </DialogContent>

        <MDBox display="flex" justifyContent="space-between" px={3} pb={2}>
          <MDButton
            onClick={() => setOpenDeleteDialog(false)}
            variant="outlined"
            color="error"
            disabled={loading}
            sx={{ minWidth: "120px" }}
          >
            Cancelar
          </MDButton>
          <MDButton
            onClick={confirmDelete}
            color="error"
            variant="gradient"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{ minWidth: "120px" }}
          >
            {loading ? "Eliminando..." : "Confirmar"}
          </MDButton>
        </MDBox>
      </Dialog>

      <CustomModal
        open={openModal}
        onClose={() => {
          setOpenModal(false);
          setError(null);
        }}
        title={`Editar zona - ${zoneSelect?.title || ""}`}
      >
        {zoneSelect && (
          <FormZone
            initialData={{
              title: zoneSelect?.title || "",
              description: zoneSelect?.description || "",
              commerceId: zoneSelect?.commerceId ?? "",
              status: zoneSelect?.status || true,
            }}
            onSubmit={handleActualizar}
            isEditMode
            loading={loading}
          />
        )}
      </CustomModal>
    </>
  );
}

TablaZone.propTypes = {
  refreshFlag: PropTypes.bool,
};

export default TablaZone;
